import { Attribute } from "../models";
import {
  CATEGORY_ORDER,
  CatalogueLayer,
  CatalogueProduct,
  LAYER_GROUP_COMPANIONS,
  LAYER_GROUP_LABELS,
  LayerGroup,
} from "./catalogue";

export type ProductSelectionState = "none" | "partial" | "all";

// A resolved billable unit of a product that bills per-group. `layers` carries
// display names for the hover tooltip.
export interface ResolvedLayerGroup extends LayerGroup {
  layers: CatalogueLayer[];
}

// Derived groups are identical for the lifetime of a product object (the
// catalogue is immutable while the query is fresh), but getLayerGroups is called
// per product per render and again inside totalSelectedCredits on every
// selection change. Cache on the product object so the derivation runs once and
// releases as soon as the query cache drops the catalogue.
const groupCache = new WeakMap<CatalogueProduct, ResolvedLayerGroup[]>();

// Products that show per-group rows (each with its own credit badge) instead of
// the plain layer list: those with more than one billable (geometry-bearing)
// layer. Returns [] for the usual single-charge products.
//
// Each billable layer heads one group and carries any aspatial companions that
// join to it (LAYER_GROUP_COMPANIONS). Labels and credits come from the
// catalogue, so adding an ABS layer or repricing a product needs no FE change.
export const getLayerGroups = (
  product: CatalogueProduct,
): ResolvedLayerGroup[] => {
  const cached = groupCache.get(product);
  if (cached) return cached;

  const byConfig = new Map(product.layers.map((l) => [l.configName, l]));
  const billable = product.layers.filter((l) => l.isBillable);
  const groups: ResolvedLayerGroup[] =
    billable.length > 1
      ? billable.map((head) => {
          const configNames = [
            head.configName,
            ...(LAYER_GROUP_COMPANIONS[head.configName] ?? []).filter((c) =>
              byConfig.has(c),
            ),
          ];
          return {
            label: LAYER_GROUP_LABELS[head.configName] ?? head.layerName,
            credits: product.credits,
            configNames,
            layers: configNames.map((c) => byConfig.get(c) as CatalogueLayer),
          };
        })
      : [];

  groupCache.set(product, groups);
  return groups;
};

// Resolve a billed layer (a QuoteSummaryItem's config_name) to the label it
// carries in the "Choose your data" picker, plus the layers that label bundles.
//
// Billing is per-layer on the backend, but the picker shows one row per product
// (single-charge products) or per billable group (G-NAF / Electoral / ABS). Since
// each product/group has exactly one billable layer, mapping that billed layer
// back to its picker label produces one summary row per product/group with no
// duplicates. Unknown config_names (legacy/retired) are absent from the index, so
// the caller keeps the raw item name and shows no include icon.
export interface SummaryRowMeta {
  label: string;
  includedLayers: string[];
}

// Build the config_name → row label index once per catalogue. Doing it per
// summary row meant scanning every product and its layers for each row.
export const buildSummaryRowIndex = (
  catalogue: CatalogueProduct[],
): Map<string, SummaryRowMeta> => {
  const index = new Map<string, SummaryRowMeta>();
  for (const product of catalogue) {
    const groups = getLayerGroups(product);
    if (groups.length > 0) {
      for (const group of groups) {
        const meta = {
          label: group.label,
          includedLayers: group.layers.map((l) => l.layerName),
        };
        for (const configName of group.configNames) {
          index.set(configName, meta);
        }
      }
      continue;
    }
    const meta = {
      label: product.productName,
      includedLayers: product.layers.map((l) => l.layerName),
    };
    for (const layer of product.layers) {
      index.set(layer.configName, meta);
    }
  }
  return index;
};

export const getGroupState = (
  group: LayerGroup,
  chosen: Set<string>,
): ProductSelectionState => {
  const hits = group.configNames.filter((c) => chosen.has(c)).length;
  if (hits === 0) return "none";
  return hits === group.configNames.length ? "all" : "partial";
};

// Tri-state → the Checkbox `checked` prop shared by the product and group rows.
export const toCheckedState = (
  state: ProductSelectionState,
): boolean | "indeterminate" =>
  state === "all" ? true : state === "partial" ? "indeterminate" : false;

// Select/deselect a whole group's layers at once, preserving any other selected
// layers of the same product.
export const toggleGroup = (
  product: CatalogueProduct,
  group: LayerGroup,
  selected: Attribute[],
  checked: boolean,
): Attribute[] => {
  const current = getSelectedLayerConfigs(product, selected);
  const groupSet = new Set(group.configNames);
  const nextConfigs = checked
    ? [...new Set([...current, ...group.configNames])]
    : current.filter((c) => !groupSet.has(c));
  const without = selected.filter((s) => s.key !== product.productKey);
  if (nextConfigs.length === 0) return without;
  return [...without, { key: product.productKey, attributes: nextConfigs }];
};

// Sub-products (those with a parentProductKey) are rendered nested under their
// parent, not as their own top-level rows.
const isSubProduct = (product: CatalogueProduct): boolean =>
  !!product.parentProductKey;

export const getChildProducts = (
  parent: CatalogueProduct,
  products: CatalogueProduct[],
): CatalogueProduct[] =>
  products.filter((p) => p.parentProductKey === parent.productKey);

const matchesSearch = (product: CatalogueProduct, term: string): boolean => {
  if (!term) return true;
  if (product.productName.toLowerCase().includes(term)) return true;
  return product.layers.some((l) => l.layerName.toLowerCase().includes(term));
};

export const groupByCategory = (
  products: CatalogueProduct[],
): { category: string; products: CatalogueProduct[] }[] => {
  const byCategory = new Map<string, CatalogueProduct[]>();
  for (const product of products) {
    // Sub-products are shown under their parent, never as top-level rows.
    if (isSubProduct(product)) continue;
    const list = byCategory.get(product.productCategory) ?? [];
    list.push(product);
    byCategory.set(product.productCategory, list);
  }
  const orderedCategories = [
    ...CATEGORY_ORDER.filter((c: string) => byCategory.has(c)),
    ...[...byCategory.keys()].filter((c: string) => !CATEGORY_ORDER.includes(c)),
  ];
  return orderedCategories.map((category) => ({
    category,
    products: byCategory.get(category) ?? [],
  }));
};

export const filterCatalogue = (
  products: CatalogueProduct[],
  search: string,
  category: string | "All",
): CatalogueProduct[] => {
  const term = search.trim().toLowerCase();
  return products.filter((product) => {
    if (isSubProduct(product)) return false;
    if (category !== "All" && product.productCategory !== category) {
      return false;
    }
    if (!term) return true;
    // A parent stays visible if it matches, or if any of its sub-products match.
    // PERF: getChildProducts filters the entire array for each product (O(n²)).
    // For large catalogues, pre-compute a parentProductKey → children map once at
    // the component level and pass it down. Current clip catalogue is ~20 products
    // so the performance impact is negligible.
    if (matchesSearch(product, term)) return true;
    return getChildProducts(product, products).some((child) =>
      matchesSearch(child, term),
    );
  });
};

const findEntry = (product: CatalogueProduct, selected: Attribute[]) =>
  selected.find((s) => s.key === product.productKey);

export const getSelectedLayerConfigs = (
  product: CatalogueProduct,
  selected: Attribute[],
): string[] => findEntry(product, selected)?.attributes ?? [];

// A product with a requiresProductKey is only selectable once the required
// product is itself selected.
export const isProductEnabled = (
  product: CatalogueProduct,
  selected: Attribute[],
): boolean => {
  if (!product.requiresProductKey) return true;
  return selected.some(
    (s) => s.key === product.requiresProductKey && s.attributes.length > 0,
  );
};

export const getProductState = (
  product: CatalogueProduct,
  selected: Attribute[],
): ProductSelectionState => {
  const entry = findEntry(product, selected);
  if (!entry || entry.attributes.length === 0) return "none";
  const allConfigs = product.layers.map((l) => l.configName);
  const hasAll = allConfigs.every((c) => entry.attributes.includes(c));
  return hasAll ? "all" : "partial";
};

export const toggleProduct = (
  product: CatalogueProduct,
  selected: Attribute[],
  checked: boolean,
  products: CatalogueProduct[] = [],
): Attribute[] => {
  let next = selected.filter((s) => s.key !== product.productKey);

  if (!checked) {
    // Deselecting a product also removes anything that depends on it: nested
    // sub-products, plus top-level products that require it.
    const dependentKeys = [
      ...getChildProducts(product, products).map((c) => c.productKey),
      ...products
        .filter((p) => p.requiresProductKey === product.productKey)
        .map((p) => p.productKey),
    ];
    if (dependentKeys.length) {
      next = next.filter((s) => !dependentKeys.includes(s.key));
    }
    return next;
  }

  next = [
    ...next,
    {
      key: product.productKey,
      attributes: product.layers.map((l) => l.configName),
    },
  ];

  // Selecting a sub-product implies selecting its parent.
  if (product.parentProductKey) {
    const parent = products.find(
      (p) => p.productKey === product.parentProductKey,
    );
    if (parent && !next.some((s) => s.key === parent.productKey)) {
      next = [
        ...next,
        {
          key: parent.productKey,
          attributes: parent.layers.map((l) => l.configName),
        },
      ];
    }
  }

  return next;
};

export const toggleLayer = (
  product: CatalogueProduct,
  configName: string,
  selected: Attribute[],
  checked: boolean,
): Attribute[] => {
  const entry = findEntry(product, selected);
  const current = entry?.attributes ?? [];
  const nextConfigs = checked
    ? [...new Set([...current, configName])]
    : current.filter((c) => c !== configName);
  const without = selected.filter((s) => s.key !== product.productKey);
  if (nextConfigs.length === 0) return without;
  return [...without, { key: product.productKey, attributes: nextConfigs }];
};

export const totalSelectedCredits = (
  products: CatalogueProduct[],
  selected: Attribute[],
): number =>
  products.reduce((sum, product) => {
    const entry = findEntry(product, selected);
    if (!entry || entry.attributes.length === 0) return sum;
    // Per-group products bill each selected group (e.g. G-NAF Active + Retired,
    // Commonwealth + State Electoral) rather than once for the product.
    const groups = getLayerGroups(product);
    if (groups.length === 0) return sum + product.credits;
    const chosen = new Set(entry.attributes);
    return (
      sum +
      groups
        .filter((group) => getGroupState(group, chosen) !== "none")
        .reduce((gSum, group) => gSum + group.credits, 0)
    );
  }, 0);

// Count only selections resolving to a current product; unresolvable stored keys
// render nothing checked, so counting them raw shows a count with 0 credits.
export const totalSelectedCount = (
  products: CatalogueProduct[],
  selected: Attribute[],
): number =>
  products.reduce((sum, product) => {
    const entry = findEntry(product, selected);
    return entry && entry.attributes.length > 0 ? sum + 1 : sum;
  }, 0);
